import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';
import { ApolloError } from '@apollo/client/errors';
import { FetchResult } from '@apollo/client';

export type NotificationType = 'error' | 'success' | 'warn';

type NotifyCommonProps = {
  type: NotificationType;
  message: string;
};

export type ErrorInfo = Error | string | ApolloError | FetchResult | unknown;

export type NotifyTypeProps = {
  readonly type: NotificationType;
  readonly message: string;
  readonly errorDetails?: ErrorInfo;
};

type NotifyProps = NotifyCommonProps & NotifyTypeProps;

type NotificationValues = {
  notification: NotifyTypeProps | null;
  notify: (props: NotifyTypeProps, clearAfter?: number) => void;
  removeNotification: () => void;
};

export const NotificationContext = createContext<NotificationValues>({
  notification: null,
  notify: () => undefined,
  removeNotification: () => undefined,
});

export default function NotifyProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [notification, setNotification] = useState<NotifyTypeProps | null>(
    null
  );
  const removeNotification = useCallback(
    () => setNotification(null),
    [setNotification]
  );

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const notify = useCallback(
    ({ type, message, errorDetails }: NotifyProps, clearAfter) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (type === 'success' || type === 'warn') {
        setNotification({ message, type });
        // Removes success notification with a delay of 4 seconds
        timerRef.current = setTimeout(removeNotification, clearAfter ?? 4000);
      } else if (type === 'error') {
        setNotification({
          message,
          type,
          errorDetails,
        });
      }
    },
    [setNotification, removeNotification]
  );

  // define global available context
  const contextValue = {
    notification,
    notify,
    removeNotification,
  };
  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}
